/**
 * Webtech HR Engine™ - Payment Return Page
 * Page de retour après paiement PayTech
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowRight,
  RefreshCw,
  Crown,
  Sparkles
} from 'lucide-react';

export default function PaymentReturnPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { refreshPlan } = usePlan();
  
  const [status, setStatus] = useState('checking'); // checking, success, failed, pending
  const [message, setMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  
  // Get payment ID from URL params or location state
  const paymentId = searchParams.get('payment_id') || location.state?.paymentId;
  const isMock = searchParams.get('mock') === 'true' || location.state?.success;

  useEffect(() => {
    if (isMock && location.state?.success) {
      // Direct success from mock payment
      setStatus('success');
      setMessage('Votre abonnement PRO est maintenant actif !');
      return;
    }
    
    if (paymentId) {
      checkPaymentStatus();
    } else {
      setStatus('failed');
      setMessage('Aucune référence de paiement trouvée');
    }
  }, [paymentId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await api.get(`/billing/payment/${paymentId}/status`);
      const paymentStatus = response.data.status;
      
      if (paymentStatus === 'completed') {
        setStatus('success');
        setMessage('Paiement confirmé ! Votre abonnement PRO est maintenant actif.');
        // Rafraîchir le plan pour mettre à jour l'UI partout
        refreshPlan();
        toast.success('🎉 Votre abonnement PRO est actif !');
      } else if (paymentStatus === 'failed') {
        setStatus('failed');
        setMessage(response.data.failureReason || 'Échec du paiement. Veuillez réessayer.');
      } else if (paymentStatus === 'pending') {
        setStatus('pending');
        setMessage('Paiement en cours de traitement...');
        
        // Retry after 5 seconds (max 6 retries = 30 seconds)
        if (retryCount < 6) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
            checkPaymentStatus();
          }, 5000);
        } else {
          setMessage('Le paiement est toujours en cours. Vous recevrez une confirmation par email.');
        }
      } else if (paymentStatus === 'cancelled') {
        setStatus('failed');
        setMessage('Paiement annulé');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      setStatus('failed');
      setMessage('Erreur lors de la vérification du paiement');
    }
  };

  const handleMockComplete = async () => {
    try {
      setStatus('checking');
      await api.post(`/billing/payment/${paymentId}/mock-complete`);
      setStatus('success');
      setMessage('Paiement simulé avec succès ! Votre abonnement PRO est actif.');
      toast.success('Abonnement activé !');
    } catch (error) {
      setStatus('failed');
      setMessage('Erreur lors de la simulation');
    }
  };

  return (
    <DashboardLayout title="Confirmation de paiement">
      <div className="max-w-lg mx-auto py-12">
        <Card>
          <CardContent className="p-8 text-center space-y-6">
            {/* Status Icon */}
            <div className="flex justify-center">
              {status === 'checking' && (
                <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                </div>
              )}
              {status === 'pending' && (
                <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center">
                  <RefreshCw className="w-10 h-10 text-amber-600 animate-spin" />
                </div>
              )}
              {status === 'success' && (
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
              )}
              {status === 'failed' && (
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
              )}
            </div>

            {/* Status Title */}
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {status === 'checking' && 'Vérification en cours...'}
                {status === 'pending' && 'Paiement en attente'}
                {status === 'success' && '🎉 Paiement réussi !'}
                {status === 'failed' && 'Paiement échoué'}
              </h1>
              <p className="text-slate-600 mt-2">{message}</p>
            </div>

            {/* Success content */}
            {status === 'success' && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                <div className="flex items-center justify-center gap-2 text-amber-800 mb-3">
                  <Crown className="w-6 h-6" />
                  <span className="font-semibold text-lg">Plan BUSINESS Activé</span>
                </div>
                <p className="text-sm text-amber-700">
                  Vous avez maintenant accès à tous les modules et fonctionnalités avancées.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col gap-3">
              {status === 'success' && (
                <>
                  <Button 
                    className="w-full bg-webtech-blue hover:bg-webtech-blue-hover"
                    onClick={() => navigate('/dashboard')}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Accéder au Dashboard
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/billing/subscription')}
                  >
                    Voir mon abonnement
                  </Button>
                </>
              )}
              
              {status === 'failed' && (
                <>
                  <Button 
                    className="w-full bg-webtech-blue hover:bg-webtech-blue-hover"
                    onClick={() => navigate('/billing/pricing')}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Réessayer
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                  >
                    Retour au Dashboard
                  </Button>
                </>
              )}
              
              {status === 'pending' && (
                <>
                  <p className="text-sm text-slate-500">
                    Nous vérifions votre paiement... ({retryCount}/6)
                  </p>
                  {/* Mock button for development */}
                  {paymentId && (
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={handleMockComplete}
                      className="text-amber-600 border-amber-300"
                    >
                      [DEV] Simuler succès
                    </Button>
                  )}
                </>
              )}
              
              {status === 'checking' && (
                <p className="text-sm text-slate-500">
                  Vérification du paiement...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
